import axios from "axios";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Button } from "./button";
import { Input } from "./input";
import { Phone, Video } from "lucide-react";
import VippsAuthenticate from "./vippsauthenticate.tsx";
import StripeAuthenticate from "./stripeauthenticate.tsx";

export function BookingSelector({ agent, onClose, baseUrl, client, currentUser, onLoginSuccess }) {
    const today = new Date();

    const default_configs = client ? JSON.parse(client.default_configs) : null;

    const [month, setMonth] = useState(today.getUTCMonth() + 1);
    const [day, setDay] = useState(today.getDate());
    const [time, setTime] = useState("");
    const [durations, setDurations] = useState([]); // ✅ list of durations from API
    const [duration, setDuration] = useState(""); // ✅ selected duration
    const [countryCode, setCountryCode] = useState("+47");
    const [phone, setPhone] = useState("");
    const [type, setType] = useState("audio");
    const [agentSlots, setAgentSlots] = useState([]);

    const [showStripe, setShowStripe] = useState(false);
    const [showVipps, setShowVipps] = useState(true);
    const [payment_method, setPaymentMethod] = useState("vipps");

    const [vippsAuthenticate, setVippsAuthenticate] = useState(false);
    const [stripeAuthenticate, setStripeAuthenticate] = useState(false);


    // 🕓 Fetch agent slot times
    const fetchAgentSlots = async () => {
        try {
            const response = await axios.get(`${baseUrl}/getAgentBookingSlotTimesByDay`, {
                params: {
                    agent_uid: agent.uid,
                    client_uid: client.uid,
                    date: `${today.getUTCFullYear()}-${month}-${day}`,
                },
            });

            if (response.data.status === "OK") {
                setAgentSlots(response.data.data);
            } else {
                setAgentSlots([]);
            }
        } catch (error) {
            console.error("Error fetching agent slots:", error);
        }
    };

    // ⏰ Fetch slot durations by time
    const fetchAgentSlotDuration = async (selectedTime) => {
        try {
            const response = await axios.get(`${baseUrl}/getAgentBookingSlotDurationsByTime`, {
                params: {
                    agent_uid: agent.uid,
                    client_uid: client.uid,
                    date: `${today.getUTCFullYear()}-${month}-${day}`,
                    time: selectedTime,
                },
            });

            if (response.data.status === "OK") {
                setDurations(response.data.data || []);
                setDuration(response.data.data?.[0] || ""); // default to first available duration
            } else {
                setDurations([]);
                setDuration("");
            }
        } catch (error) {
            console.error("Error fetching durations:", error);
        }
    };

    // 🔄 Refetch when month/day changes
    useEffect(() => {
        fetchAgentSlots();
    }, [client, day, month]);

    // 🕒 Set default time when slots update
    useEffect(() => {
        if (agentSlots?.length > 0) {
            setTime(agentSlots[0]);
            fetchAgentSlotDuration(agentSlots[0]);
        } else {
            setTime("");
            setDurations([]);
        }
    }, [agentSlots]);

    // 📅 Generate 3 months (current + next 2)
    const months = [];
    for (let i = 0; i < 3; i++) {
        const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
        const monthName = date.toLocaleString("default", { month: "long" });
        months.push({ name: monthName, value: date.getMonth() + 1 });
    }

    useEffect(() => {
        const channel = new BroadcastChannel("chamcore-booking-approve-payment");
        channel.onmessage = (event) => {
            if (event.data?.type === "BOOKING_CARD_PAYMENT_APPROVED") {
                onClose();
                Swal.fire({
                    icon: "success",
                    title: "Vellykket betaling",
                    text: "Betalingen for timebestillingen er vellykket.",
                    showConfirmButton: true,
                });
                setStripeAuthenticate(false);
                if (typeof onLoginSuccess === "function") {
                    onLoginSuccess(event.data?.token, event.data?.role, event.data?.name);
                }
            }
        };
        return () => channel.close();
    }, []);

    const handleBooking = async () => {
        var formData = new FormData();
        formData.append("agent_uid", agent.uid);
        formData.append("payment_method", payment_method);
        formData.append("month", month.toString());
        formData.append("day", day.toString());
        formData.append("duration", duration.toString());
        formData.append("time", time.toString());
        formData.append("type", type.toString());
        if (currentUser) {
            formData.append("token", currentUser?.token);
        } else {
            formData.append("country_code", countryCode);
            formData.append("phone", phone);
        }

        if (payment_method === "vipps") {
            setVippsAuthenticate(true);
        } else {
            setStripeAuthenticate(true);
        }

        try {
            const response = await axios.post(baseUrl + '/addeventagent', formData);

            if (response.data.status === "OK") {
                if (response.data.stripe_checkout && response.data.stripe_checkout === "yes") {
                    window.open(response.data.message, "_blank");
                } else {
                    Swal.fire({
                        icon: "success",
                        title: "Success",
                        text: response.data.message,
                        showConfirmButton: true,
                    });
                    onClose();
                    if (payment_method === "vipps") {
                        setVippsAuthenticate(false);

                        if (typeof onLoginSuccess === "function") {
                            onLoginSuccess(response.data.token, response.data.role, response.data.name);
                        }
                    } else {
                        setStripeAuthenticate(false);
                    }
                }
            } else if (response.data.status === "ABORTED") {
                Swal.fire({
                    icon: "warning",
                    title: "Warning",
                    text: "Customer aborted the authentication",
                    showConfirmButton: true,
                });
                if (payment_method === "vipps") {
                    setVippsAuthenticate(false);
                } else {
                    setStripeAuthenticate(false);
                }
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: response.data.message,
                    showConfirmButton: true,
                });
                if (payment_method === "vipps") {
                    setVippsAuthenticate(false);
                } else {
                    setStripeAuthenticate(false);
                }
            }
        } catch (error) {
            console.error(error);
            if (payment_method === "vipps") {
                setVippsAuthenticate(false);
            } else {
                setStripeAuthenticate(false);
            }
        } finally {
            // onClose();
        }

    };

    const handleNoVipps = (e) => {
        //hide vipps button and show stripe button
        e.preventDefault();
        setShowVipps(false);
        setShowStripe(true);
        setPaymentMethod("stripe");
    }
    const handleNoStripe = (e) => {
        //hide stripe button and show vipps button
        e.preventDefault();
        setShowVipps(true);
        setShowStripe(false);
        setPaymentMethod("vipps");
    }



    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full relative">
                {vippsAuthenticate ? (
                    <VippsAuthenticate vippsAuthenticate={vippsAuthenticate} />
                ) : stripeAuthenticate ? (
                    <StripeAuthenticate stripeAuthenticate={stripeAuthenticate} />
                ) : (
                    <>
                        <button onClick={onClose} className="absolute right-4 top-4 text-gray-500">
                            ✕
                        </button>

                        <h2 className="text-xl font-semibold mb-4">
                            Book an appointment with {agent.name}
                        </h2>

                        {/* Month, Day, and Time Selectors */}
                        <div className="grid grid-cols-3 gap-2 mb-3">
                            <select
                                value={month}
                                onChange={(e) => setMonth(Number(e.target.value))}
                                className="border rounded-lg p-2"
                            >
                                {months.map((m, i) => (
                                    <option key={i} value={m.value}>
                                        {m.name}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={day}
                                onChange={(e) => setDay(Number(e.target.value))}
                                className="border rounded-lg p-2"
                            >
                                {Array.from({ length: 31 }, (_, i) => (
                                    <option key={i + 1} value={i + 1}>
                                        {i + 1}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={time}
                                onChange={(e) => {
                                    const newTime = e.target.value.trim();
                                    setTime(newTime);
                                    fetchAgentSlotDuration(newTime);
                                }}
                                className="border rounded-lg p-2"
                            >
                                {agentSlots?.length > 0 ? (
                                    agentSlots.map((slot, i) => (
                                        <option key={i} value={slot}>
                                            {slot}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>No slots available</option>
                                )}
                            </select>
                        </div>

                        {/* Duration Selector */}
                        <div className="mb-4">
                            <label className="font-semibold text-sm block mb-1">Duration</label>
                            <select
                                value={duration}
                                onChange={(e) => setDuration(e.target.value)}
                                className="w-full border rounded-lg p-2"
                            >
                                <option value="">Select duration</option>
                                {durations?.length > 0 ? (
                                    durations.map((d, i) => (
                                        <option key={i} value={d}>
                                            {d} minutes
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>No durations available</option>
                                )}
                            </select>
                        </div>

                        {/* Phone fields (for non-customer users) */}
                        {!currentUser || currentUser?.role !== "customer" ? (
                            <div className="grid grid-cols-2 gap-2 mb-4">
                                <div>
                                    <label className="font-semibold text-sm block mb-1">
                                        Country Code
                                    </label>
                                    <Input
                                        value={countryCode}
                                        onChange={(e) => setCountryCode(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="font-semibold text-sm block mb-1">
                                        Phone Number
                                    </label>
                                    <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
                                </div>
                            </div>
                        ) : null}

                        {/* Call Type Buttons */}
                        <div className="mb-4">
                            <label className="font-semibold text-sm block mb-2">Type</label>
                            <div className={ default_configs?.video === "yes" && agent?.can_video_call === "1" ? "grid grid-cols-2 gap-3" : "grid grid-cols-1 gap-3"} >
                                <button
                                    onClick={() => setType("audio")}
                                    className={`border rounded-lg flex items-center justify-center p-3 transform transition-all duration-300 hover:scale-105 hover:shadow-lg ${type === "audio"
                                        ? "bg-orange-50 border-orange-400"
                                        : "border-gray-300"
                                        }`}
                                >
                                    <Phone className="w-5 h-5" />
                                </button>

                                {default_configs?.video === "yes" && agent?.can_video_call === "1" ? (
                                    <button
                                        onClick={() => setType("video")}
                                        className={`border rounded-lg flex items-center justify-center p-3 transform transition-all duration-300 hover:scale-105 hover:shadow-lg ${type === "video"
                                            ? "bg-orange-50 border-orange-400"
                                            : "border-gray-300"
                                            }`}
                                    >
                                        <Video className="w-5 h-5" />
                                    </button>
                                ) : ('')}
                            </div>
                        </div>

                        {/* Vipps Button */}
                        {showVipps ? (<Button
                            className="bg-[#FF5B24] w-full py-6 text-lg font-semibold transform transition-all duration-300 hover:scale-105 hover:shadow-x"
                            onClick={handleBooking}
                        >
                            Continue with <span className="ml-2 font-bold">Vipps</span>
                        </Button>) : ('')}

                        {showStripe ? (<Button
                            className="bg-[#FF5B24] w-full py-6 text-lg font-semibold transform transition-all duration-300 hover:scale-105 hover:shadow-x"
                            onClick={handleBooking}
                        >Continue with <span className="ml-2 font-bold">Stripe</span></Button>) : ('')}


                        {showVipps ? (<a href="#" onClick={handleNoVipps} >Don't have vipps?</a>) : ('')}
                        {showStripe ? (<a href="#" onClick={handleNoStripe} >Don't have stripe?</a>) : ('')}
                    </>
                )}
            </div>
        </div>
    );
}